import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/authService';
import { generateDocumentId } from '../utils/idGenerator';
import { useAuth } from './AuthContext';

// Create the context
export const DocumentContext = createContext(null);

// Custom hook to use the document context
export const useDocuments = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocuments must be used within a DocumentProvider');
  }
  return context;
};

// Document provider component
export const DocumentProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();

  // Fetch documents - memoized to prevent unnecessary rerenders
  const fetchDocuments = useCallback(async (userId) => {
    if (!userId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch documents from Supabase
      const { data, error: fetchError } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (fetchError) {
        console.error('Error fetching documents:', fetchError);
        throw fetchError;
      }
      
      setDocuments(data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
      setLoading(false);
    }
  }, []);

  // Load documents when user changes
  useEffect(() => {
    let isMounted = true;
    
    const loadDocuments = async () => {
      if (!isAuthenticated || !user) {
        if (isMounted) {
          setDocuments([]);
          setLoading(false);
        }
        return;
      }

      try {
        await fetchDocuments(user.id);
      } catch (err) {
        console.error('Error in loadDocuments:', err);
        if (isMounted) {
          setError('Failed to load documents');
          setLoading(false);
        }
      }
    };

    // Set a timeout to ensure loading state doesn't get stuck
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 3000); // 3 second timeout

    loadDocuments();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, user, fetchDocuments]);

  // Create a new document
  const createDocument = async (documentData) => {
    if (!isAuthenticated || !user) {
      throw new Error('You must be logged in to create documents');
    }

    try {
      const documentId = generateDocumentId();
      
      // 1. Upload file to Supabase Storage
      let fileUrl = null;
      if (documentData.fileData) {
        // Convert base64 to blob
        const base64Response = await fetch(documentData.fileData);
        const blob = await base64Response.blob();
        
        // Create file path in storage
        const filePath = `${user.id}/${documentId}/${documentData.fileName || 'document.pdf'}`;
        
        // Upload to Supabase Storage
        const { data: fileData, error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, blob, {
            contentType: documentData.fileType || 'application/pdf',
            upsert: true
          });
        
        if (uploadError) {
          console.error('Error uploading file to storage:', uploadError);
          throw uploadError;
        }
        
        // Get signed URL for the file (for private buckets)
        const { data: { signedUrl }, error: signedUrlError } = await supabase.storage
          .from('documents')
          .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 days expiry
        
        if (signedUrlError) {
          console.error('Error creating signed URL:', signedUrlError);
          throw signedUrlError;
        }
        
        fileUrl = signedUrl;
      }
      
      // 2. Create document record in database
      const newDocument = {
        id: documentId,
        user_id: user.id,
        name: documentData.name || 'Untitled Document',
        // Store both the file data and URL in the file_data field
        file_data: fileUrl ? 
          `STORAGE_URL:${fileUrl}` : 
          (documentData.fileData ? 
            documentData.fileData.substring(0, 1000) + '... [truncated]' : 
            'data:application/pdf;base64,JVBERi0xLjcKJeLjz9MKNSAwIG9iago8PC9GaWx0ZXIvRmxhdGVEZWNvZGUvTGVuZ3RoIDM4Pj4Kc3RyZWFtCnicK+QCAjMgBCTMzC3kggDTQC5DLkMuAyMDIwNDLgMTAyNDQy5DAJHnBooKZW5kc3RyZWFtCmVuZG9iago0IDAgb2JqCjw8L0NvbnRlbnRzIDUgMCBSL01lZGlhQm94WzAgMCA1OTUgODQyXS9QYXJlbnQgMiAwIFIvUmVzb3VyY2VzPDwvRm9udDw8L0YxIDYgMCBSPj4+Pi9UcmltQm94WzAgMCA1OTUgODQyXS9UeXBlL1BhZ2U+PgplbmRvYmoKMSAwIG9iago8PC9QYWdlcyAyIDAgUi9UeXBlL0NhdGFsb2c+PgplbmRvYmoKMyAwIG9iago8PC9BdXRob3IoKS9DcmVhdGlvbkRhdGUoRDoyMDIzMDQwMzEwMDg0NCswMCcwMCcpL0NyZWF0b3IoTWljcm9zb2Z0OiBQcmludCBUbyBQREYpL01vZERhdGUoRDoyMDIzMDQwMzEwMDg0NCswMCcwMCcpL1Byb2R1Y2VyKGlMb3ZlUERGKT4+CmVuZG9iagoyIDAgb2JqCjw8L0NvdW50IDEvS2lkc1s0IDAgUl0vVHlwZS9QYWdlcz4+CmVuZG9iago2IDAgb2JqCjw8L0Jhc2VGb250L0hlbHZldGljYS9FbmNvZGluZy9XaW5BbnNpRW5jb2RpbmcvU3VidHlwZS9UeXBlMS9UeXBlL0ZvbnQ+PgplbmRvYmoKeHJlZgowIDcKMDAwMDAwMDAwMCA2NTUzNSBmIAowMDAwMDAwMTg3IDAwMDAwIG4gCjAwMDAwMDAzNzYgMDAwMDAgbiAKMDAwMDAwMDIzMiAwMDAwMCBuIAowMDAwMDAwMDk1IDAwMDAwIG4gCjAwMDAwMDAwMTUgMDAwMDAgbiAKMDAwMDAwMDQyNyAwMDAwMCBuIAp0cmFpbGVyCjw8L0lEIFs8ZWVmODQ5MjRmYjM1MjQwYWJmNzY0MzRhYTZiZjI3MWM+PGVlZjg0OTI0ZmIzNTI0MGFiZjc2NDM0YWE2YmYyNzFjPl0vSW5mbyAzIDAgUi9Sb290IDEgMCBSL1NpemUgNz4+CnN0YXJ0eHJlZgo1MTQKJSVFT0YK'),
        file_type: documentData.fileType || 'application/pdf',
        status: 'draft',
        fields: documentData.fields || [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error: insertError } = await supabase
        .from('documents')
        .insert(newDocument)
        .select()
        .single();
      
      if (insertError) {
        console.error('Error inserting document record:', insertError);
        throw insertError;
      }
      
      // 3. Add to state
      setDocuments(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error creating document:', err);
      setError('Failed to create document');
      throw err;
    }
  };

  // Update an existing document
  const updateDocument = async (documentId, documentData) => {
    if (!isAuthenticated || !user) {
      throw new Error('You must be logged in to update documents');
    }

    try {
      const existingDocument = documents.find(doc => doc.id === documentId);
      
      if (!existingDocument) {
        throw new Error(`Document with ID ${documentId} not found`);
      }
      
      // Ensure user owns the document
      if (existingDocument.user_id !== user.id) {
        throw new Error('You do not have permission to update this document');
      }
      
      // If there's a new file to upload
      if (documentData.fileData) {
        // Convert base64 to blob
        const base64Response = await fetch(documentData.fileData);
        const blob = await base64Response.blob();
        
        // Create file path in storage
        const filePath = `${user.id}/${documentId}/${documentData.fileName || 'document.pdf'}`;
        
        // Upload to Supabase Storage
        const { data: fileData, error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, blob, {
            contentType: documentData.fileType || 'application/pdf',
            upsert: true
          });
        
        if (uploadError) {
          console.error('Error uploading file to storage:', uploadError);
          throw uploadError;
        }
        
        // Get signed URL for the file (for private buckets)
        const { data: { signedUrl }, error: signedUrlError } = await supabase.storage
          .from('documents')
          .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 days expiry
        
        if (signedUrlError) {
          console.error('Error creating signed URL:', signedUrlError);
          throw signedUrlError;
        }
        
        // Store the file URL in the file_data field
        documentData.file_data = `STORAGE_URL:${signedUrl}`;
      }
      
      // Prepare update data
      const updateData = {
        ...documentData,
        updated_at: new Date().toISOString()
      };
      
      // Update in Supabase
      const { data, error: updateError } = await supabase
        .from('documents')
        .update(updateData)
        .eq('id', documentId)
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (updateError) {
        console.error('Error updating document in database:', updateError);
        throw updateError;
      }
      
      // Update state
      setDocuments(prev => 
        prev.map(doc => doc.id === documentId ? data : doc)
      );
      
      return data;
    } catch (err) {
      console.error('Error updating document:', err);
      setError('Failed to update document');
      throw err;
    }
  };

  // Remove a document
  const removeDocument = async (documentId) => {
    if (!isAuthenticated || !user) {
      throw new Error('You must be logged in to delete documents');
    }

    try {
      const existingDocument = documents.find(doc => doc.id === documentId);
      
      if (!existingDocument) {
        throw new Error(`Document with ID ${documentId} not found`);
      }
      
      // 1. Delete file from storage if it exists
      // Check if file_data contains a storage URL
      const fileData = existingDocument.file_data || '';
      const isStorageUrl = fileData.startsWith('STORAGE_URL:');
      
      if (isStorageUrl) {
        // Extract the path from the URL
        const filePath = `${user.id}/${documentId}`;
        
        // List all files in the document folder
        const { data: fileList, error: listError } = await supabase.storage
          .from('documents')
          .list(filePath);
        
        if (listError) {
          console.error('Error listing files in storage:', listError);
          // Continue with deletion even if listing fails
        } else if (fileList && fileList.length > 0) {
          // Delete all files in the folder
          const filesToDelete = fileList.map(file => `${filePath}/${file.name}`);
          const { error: deleteFileError } = await supabase.storage
            .from('documents')
            .remove(filesToDelete);
          
          if (deleteFileError) {
            console.error('Error deleting files from storage:', deleteFileError);
            // Continue with database deletion even if file deletion fails
          }
        }
      }
      
      // 2. Delete document from database
      const { error: deleteError } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId)
        .eq('user_id', user.id);
      
      if (deleteError) {
        console.error('Error deleting document from database:', deleteError);
        throw deleteError;
      }
      
      // 3. Update state
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      return true;
    } catch (err) {
      console.error('Error removing document:', err);
      setError('Failed to remove document');
      throw err;
    }
  };

  // Add a field to a document
  const addField = async (documentId, field) => {
    try {
      const document = documents.find(doc => doc.id === documentId);
      
      if (!document) {
        throw new Error(`Document with ID ${documentId} not found`);
      }
      
      const updatedFields = [...(document.fields || []), field];
      
      return await updateDocument(documentId, { fields: updatedFields });
    } catch (err) {
      console.error('Error adding field to document:', err);
      setError('Failed to add field');
      throw err;
    }
  };

  // Update a field in a document
  const updateField = async (documentId, fieldId, fieldData) => {
    try {
      const document = documents.find(doc => doc.id === documentId);
      
      if (!document) {
        throw new Error(`Document with ID ${documentId} not found`);
      }
      
      const fields = document.fields || [];
      const fieldIndex = fields.findIndex(f => f.id === fieldId);
      
      if (fieldIndex === -1) {
        throw new Error(`Field with ID ${fieldId} not found in document ${documentId}`);
      }
      
      const updatedFields = [...fields];
      updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...fieldData };
      
      return await updateDocument(documentId, { fields: updatedFields });
    } catch (err) {
      console.error('Error updating field in document:', err);
      setError('Failed to update field');
      throw err;
    }
  };

  // Remove a field from a document
  const removeField = async (documentId, fieldId) => {
    try {
      const document = documents.find(doc => doc.id === documentId);
      
      if (!document) {
        throw new Error(`Document with ID ${documentId} not found`);
      }
      
      const fields = document.fields || [];
      const updatedFields = fields.filter(f => f.id !== fieldId);
      
      return await updateDocument(documentId, { fields: updatedFields });
    } catch (err) {
      console.error('Error removing field from document:', err);
      setError('Failed to remove field');
      throw err;
    }
  };

  // Mark a document as sent
  const markDocumentAsSent = async (documentId, recipientEmail) => {
    try {
      return await updateDocument(documentId, { 
        status: 'sent',
        sent_at: new Date().toISOString(),
        recipient_email: recipientEmail
      });
    } catch (err) {
      console.error('Error marking document as sent:', err);
      setError('Failed to update document status');
      throw err;
    }
  };

  // Mark a document as signed
  const markDocumentAsSigned = async (documentId, signedData) => {
    try {
      return await updateDocument(documentId, { 
        status: 'signed',
        signed_at: new Date().toISOString(),
        signed_data: signedData
      });
    } catch (err) {
      console.error('Error marking document as signed:', err);
      setError('Failed to update document status');
      throw err;
    }
  };

  // Clear any error
  const clearError = () => {
    setError(null);
  };

  // The context value
  const value = {
    documents,
    loading,
    error,
    createDocument,
    updateDocument,
    removeDocument,
    addField,
    updateField,
    removeField,
    markDocumentAsSent,
    markDocumentAsSigned,
    clearError
  };

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};
